/**
 * =============================================================================
 * CONFIG PANEL - RIGHT-SIDE STEP CONFIGURATION FORM
 * =============================================================================
 * 
 * PURPOSE:
 * This component renders the configuration panel that slides in from the right
 * when a workflow step is selected. It displays different forms based on step type.
 * 
 * RESPONSIBILITIES:
 * 1. Show/hide animation based on showConfigPanel prop
 * 2. Display header with icon and title based on step type
 * 3. Render appropriate config form based on configType
 * 4. Handle close action
 * 
 * RELATIONSHIP TO WORKFLOW BUILDER:
 * - Opens when a WorkflowNode is clicked (onNodeClick in AutomationsContainer)
 * - configType determines which form to show (trigger, email, sms, wait)
 * - Closing resets selectedNode and configType in parent state
 * 
 * CONFIG CONTENT TYPES:
 * - 'trigger': TriggerConfigContent - Entry point configuration
 * - 'sms': SmsConfigContent - SMS message configuration
 * - 'email': EmailConfigContent - Email message configuration
 * - 'wait': WaitConfigContent - Delay/wait configuration
 * 
 * LAYOUT:
 * - Desktop: Fixed 380px wide panel, slides in from right
 * - Mobile: Full-screen overlay, slides up from bottom
 * 
 * DATA FLOW:
 * User clicks WorkflowNode → onNodeClick(type, id) → 
 * AutomationsContainer sets configType & showConfigPanel →
 * ConfigPanel renders with appropriate form
 * 
 * NOTE FOR DRAG-AND-DROP:
 * - This component is NOT directly affected by DnD implementation
 * - However, after adding a new step via drag, you might want to:
 *   1. Auto-open ConfigPanel for the newly added step
 *   2. Pass the new step's type to configType
 * =============================================================================
 */
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { getConfigHeader, insertAtCursor } from '../helpers';
import {
  TriggerConfigContent,
  SmsConfigContent,
  EmailConfigContent,
  WaitConfigContent,
} from './configs';

/**
 * ConfigContent - Internal component that switches between config forms
 * 
 * Acts as a simple switch/router based on configType.
 * Each config form is its own component with its own state/logic.
 * 
 * @param {Object} props
 * @param {string} props.configType - Type of step being configured
 * @param {Array} props.leads - Leads data for trigger config
 * @param {Object} props.triggerConfig - Current trigger configuration
 * @param {Function} props.onTriggerConfigChange - Handler for trigger config changes
 * @param {Array} props.forms - Available forms for form-based trigger
 */
const ConfigContent = ({
  configType,
  leads,
  triggerConfig,
  onTriggerConfigChange,
  forms,
  draftConfig,
  onDraftConfigChange,
  subjectRef,
  emailBodyRef,
  smsBodyRef,
  onFocusField,
  onInsertVariable,
}) => {
  // Switch statement maps step type to appropriate config form component
  switch (configType) {
    case 'sms':
      return (
        <SmsConfigContent
          body={draftConfig?.body || ''}
          bodyRef={smsBodyRef}
          onFocusField={onFocusField}
          onInsertVariable={onInsertVariable}
          onChange={(updates) => onDraftConfigChange(updates)}
        />
      );
    case 'email':
      return (
        <EmailConfigContent
          subject={draftConfig?.subject || ''}
          body={draftConfig?.body || ''}
          attachments={draftConfig?.attachments || []}
          subjectRef={subjectRef}
          bodyRef={emailBodyRef}
          onFocusField={onFocusField}
          onInsertVariable={onInsertVariable}
          onChange={(updates) => onDraftConfigChange(updates)}
        />
      );
    case 'wait':
      return (
        <WaitConfigContent
          delaySeconds={draftConfig?.delaySeconds}
          waitUnit={draftConfig?.waitUnit}
          waitValue={draftConfig?.waitValue}
          onChange={(updates) => onDraftConfigChange(updates)}
        />
      );
    case 'trigger':
      return (
        <TriggerConfigContent
          leads={leads}
          initialConfig={triggerConfig}
          onConfigChange={onTriggerConfigChange}
          forms={forms}
        />
      );
    default:
      return null;  // Unknown type - render nothing
  }
};

/**
 * ConfigPanel Component - The sliding configuration panel
 * 
 * @param {Object} props
 * @param {boolean} props.showConfigPanel - Whether panel is visible
 * @param {string|null} props.configType - Type of step being configured
 * @param {Function} props.onClose - Handler to close the panel
 * @param {boolean} props.isMobile - Whether to render mobile layout
 * @param {Array} props.leads - Leads data for trigger config
 * @param {Object} props.triggerConfig - Current trigger configuration
 * @param {Function} props.onTriggerConfigChange - Handler for trigger config changes
 * @param {Array} props.forms - Available forms for form-based trigger
 * @param {Object|null} props.selectedStep - Selected step object for config editing
 * @param {Function} props.onSaveNodeConfig - Persist node config on Done
 */
const ConfigPanel = ({
  showConfigPanel,
  configType,
  onClose,
  isMobile,
  leads = [],
  triggerConfig = {},
  onTriggerConfigChange,
  forms = [],
  selectedStep = null,
  onSaveNodeConfig,
  isReadOnly = false,
}) => {
  const header = getConfigHeader(configType);
  const [draftConfig, setDraftConfig] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Initialize draft config when selection changes
  useEffect(() => {
    setSaveError(null);
    setIsSaving(false); // Reset saving state when panel opens
    if (!selectedStep || !configType) {
      setDraftConfig(null);
      return;
    }

    const config = selectedStep.config || {};

    if (configType === 'email') {
      setDraftConfig({
        to: config.to || '',
        subject: config.subject || '',
        body: config.body || '',
        attachments: config.attachments || [],
      });
      return;
    }

    if (configType === 'sms') {
      setDraftConfig({
        to: config.to || '',
        body: config.body || config.message || '',
      });
      return;
    }

    if (configType === 'wait') {
      const parsed = parseInt(config.delaySeconds, 10);
      setDraftConfig({
        delaySeconds: Number.isFinite(parsed) ? parsed : 86400,
        waitUnit: config.waitUnit || undefined,
        waitValue: config.waitValue !== undefined ? config.waitValue : undefined,
      });
      return;
    }

    setDraftConfig(null);
  }, [selectedStep, configType]);

  // Cleanup saving state on unmount
  useEffect(() => {
    return () => setIsSaving(false);
  }, []);

  // Reset saving state whenever panel visibility changes (panel may be hidden but not unmounted on desktop)
  useEffect(() => {
    if (showConfigPanel) {
      setIsSaving(false);
      setSaveError(null);
    }
  }, [showConfigPanel]);

  const subjectRef = React.useRef(null);
  const emailBodyRef = React.useRef(null);
  const smsBodyRef = React.useRef(null);

  const [activeEditor, setActiveEditor] = useState(null);

  const handleDraftConfigChange = (updates) => {
    if (isReadOnly) return; // Block config changes in read-only mode
    setDraftConfig(prev => ({ ...(prev || {}), ...updates }));
  };

  const handleFocusField = (fieldName) => {
    // fieldName: 'subject' | 'body' (email), 'body' (sms)
    // Map to active editor key based on configType
    if (configType === 'email') {
      setActiveEditor(fieldName === 'subject' ? 'emailSubject' : 'emailBody');
    } else if (configType === 'sms') {
      setActiveEditor('smsBody');
    }
  };

  const handleInsertVariable = (token) => {
    // Determine target editor
    let target = activeEditor;
    if (!target) {
      // Default target: prefer body for both email and sms
      target = configType === 'email' ? 'emailBody' : 'smsBody';
    }

    if (target === 'emailSubject') {
      const ref = subjectRef.current;
      const current = draftConfig?.subject || '';
      const { value, cursor } = insertAtCursor(ref, current, token);
      handleDraftConfigChange({ subject: value });
      setTimeout(() => {
        if (ref && ref.setSelectionRange) ref.setSelectionRange(cursor, cursor);
      }, 0);
      setActiveEditor('emailSubject');
      return;
    }

    if (target === 'emailBody') {
      const ref = emailBodyRef.current;
      // Rich text editor: use insertVariable method if available
      if (ref && typeof ref.insertVariable === 'function') {
        ref.insertVariable(token);
      } else {
        const current = draftConfig?.body || '';
        const { value, cursor } = insertAtCursor(ref, current, token);
        handleDraftConfigChange({ body: value });
        setTimeout(() => {
          if (ref && ref.setSelectionRange) ref.setSelectionRange(cursor, cursor);
        }, 0);
      }
      setActiveEditor('emailBody');
      return;
    }

    if (target === 'smsBody') {
      const ref = smsBodyRef.current;
      const current = draftConfig?.body || '';
      const { value, cursor } = insertAtCursor(ref, current, token);
      handleDraftConfigChange({ body: value });
      setTimeout(() => {
        if (ref && ref.setSelectionRange) ref.setSelectionRange(cursor, cursor);
      }, 0);
      setActiveEditor('smsBody');
      return;
    }
  };
  const handleDone = async () => {
    // In read-only mode, Done just closes the panel (no save)
    if (isReadOnly) {
      onClose();
      return;
    }

    if (isSaving) return; // Prevent duplicate click-saves

    if (configType === 'trigger') {
      onClose();
      return;
    }

    if (!selectedStep?.id || !onSaveNodeConfig || !draftConfig) {
      setSaveError(new Error('No step selected for saving.'));
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      await onSaveNodeConfig(selectedStep.id, draftConfig || {});
      onClose(); // Close only after successful save
    } catch (err) {
      setSaveError(err);
    } finally {
      setIsSaving(false); // Always reset saving state
    }
  };

  const isDoneDisabled = !isReadOnly && (isSaving || (configType !== 'trigger' && (!selectedStep || !draftConfig)));

  if (isMobile) {
    if (!showConfigPanel) return null;

    return (
      <div className="absolute inset-0 z-40 bg-white flex flex-col animate-slideUp">
        <div className="h-14 border-b border-notion-border px-4 flex items-center justify-between flex-shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded flex items-center justify-center ${header.bgColor} ${header.iconColor}`}>
              {React.createElement(header.icon, { className: 'w-4 h-4' })}
            </div>
            <span className="font-bold text-stone-800 text-sm">{header.title}</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-full text-stone-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6 bg-stone-50/30">
          <ConfigContent
            configType={configType}
            leads={leads}
            triggerConfig={triggerConfig}
            onTriggerConfigChange={onTriggerConfigChange}
            forms={forms}
            draftConfig={draftConfig}
            onDraftConfigChange={handleDraftConfigChange}
            subjectRef={subjectRef}
            emailBodyRef={emailBodyRef}
            smsBodyRef={smsBodyRef}
            onFocusField={handleFocusField}
            onInsertVariable={handleInsertVariable}
          />
        </div>

        <div className="p-4 border-t border-notion-border bg-white flex-shrink-0 pb-8">
          {isReadOnly && (
            <div className="mb-2 text-xs text-amber-600 font-medium text-center">
              View only — stop automation to edit
            </div>
          )}
          {saveError && (
            <div className="mb-2 text-xs text-red-600">
              {saveError.message || saveError || 'Failed to save step. Please try again.'}
            </div>
          )}
          <button
            onClick={handleDone}
            disabled={isDoneDisabled}
            className={`w-full py-3 rounded-xl text-sm font-medium shadow-sm disabled:opacity-60 ${
              isReadOnly
                ? 'bg-stone-200 text-stone-600'
                : 'bg-black text-white'
            }`}
          >
            {isReadOnly ? 'Close' : isSaving ? 'Saving...' : 'Done'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-[380px] bg-white border-l border-notion-border flex flex-col z-20 shadow-floating absolute right-0 top-0 bottom-0 transform transition-transform duration-300 ${showConfigPanel ? 'translate-x-0' : 'translate-x-full'}`}>
      <div className="h-16 border-b border-notion-border px-6 flex items-center justify-between flex-shrink-0 bg-white">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded flex items-center justify-center ${header.bgColor} ${header.iconColor}`}>
            {React.createElement(header.icon, { className: 'w-4 h-4' })}
          </div>
          <span className="font-bold text-stone-800 text-sm">{header.title}</span>
        </div>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-stone-50/30">
        <ConfigContent
          configType={configType}
          leads={leads}
          triggerConfig={triggerConfig}
          onTriggerConfigChange={onTriggerConfigChange}
          forms={forms}
          draftConfig={draftConfig}
          onDraftConfigChange={handleDraftConfigChange}
          subjectRef={subjectRef}
          emailBodyRef={emailBodyRef}
          smsBodyRef={smsBodyRef}
          onFocusField={handleFocusField}
          onInsertVariable={handleInsertVariable}
        />
      </div>

      <div className="p-6 border-t border-notion-border bg-white flex-shrink-0">
        {isReadOnly && (
          <div className="mb-2 text-xs text-amber-600 font-medium text-center">
            View only — stop automation to edit
          </div>
        )}
        {saveError && (
          <div className="mb-2 text-xs text-red-600">
            {saveError.message || saveError || 'Failed to save step. Please try again.'}
          </div>
        )}
        <button
          onClick={handleDone}
          disabled={isDoneDisabled}
          className={`w-full py-2.5 rounded-lg text-xs font-medium transition shadow-sm disabled:opacity-60 ${
            isReadOnly
              ? 'bg-stone-200 text-stone-600 hover:bg-stone-300'
              : 'bg-black text-white hover:bg-stone-800'
          }`}
        >
          {isReadOnly ? 'Close' : isSaving ? 'Saving...' : 'Done'}
        </button>
      </div>
    </div>
  );
};

export default ConfigPanel;
